import { useCallback, useEffect, useRef, useState } from "react";
import type { Room } from "livekit-client";
import { groupCallsUnavailable, type GroupCallEntry } from "../../shared/api";
import type { GroupLink } from "../../shared/types";
import { notify } from "../ui/toast";

export type LiveGroupCall = { link: GroupLink; room: Room; organizer: string };
export type GroupCallHolder = {
  live: LiveGroupCall | null;
  enter: (entry: GroupCallEntry) => Promise<void>;
  leave: () => void;
};

// The shell's one group call. The SDK owns reconnection and media, and stops the
// microphone whenever the room disconnects, so all this holds is the room. Its
// library loads here, the first time someone joins.
export function useGroupCall(): GroupCallHolder {
  const [live, setLive] = useState<LiveGroupCall | null>(null);
  const current = useRef<Room | null>(null);
  const forget = useCallback((room: Room) => {
    if (current.current !== room) return false;
    current.current = null;
    setLive(null);
    return true;
  }, []);
  const leave = useCallback(() => {
    const room = current.current;
    if (room && forget(room)) void room.disconnect();
  }, [forget]);
  useEffect(() => leave, [leave]);
  const enter = useCallback(
    async ({ link, url, token, organizer }: GroupCallEntry) => {
      const { DisconnectReason, Room, RoomEvent } =
        await import("livekit-client");
      const room = new Room();
      let connected = false,
        mine = false;
      room.on(RoomEvent.Disconnected, (reason) => {
        // Leaving forgets the room first, and failing to connect fires this too
        // and gets its own message below, so only a lost connection or an ended
        // group call gets here. The Worker deletes a room only when the organizer
        // ends it, and they know already.
        if (!connected || !forget(room)) return;
        if (reason !== DisconnectReason.ROOM_DELETED)
          notify("انقطع اتصالك بالمكالمة الجماعية.");
        else if (!mine) notify("أنهى المنظّم المكالمة الجماعية.");
      });
      current.current = room;
      setLive({ link, room, organizer });
      try {
        await room.connect(url, token);
        connected = true;
        mine = room.localParticipant.identity === organizer;
      } catch (error) {
        // Leaving while connecting cancels it, and that needs no message.
        if (!forget(room)) return;
        void room.disconnect();
        throw new Error(groupCallsUnavailable, { cause: error });
      }
      try {
        await room.localParticipant.setMicrophoneEnabled(true);
      } catch (error) {
        if (!forget(room)) return;
        void room.disconnect();
        throw new Error(
          "لا يمكن الانضمام دون الميكروفون. اسمح باستخدامه من إعدادات المتصفح.",
          { cause: error },
        );
      }
    },
    [forget],
  );
  return { live, enter, leave };
}
